import React, { Component } from 'react';
import Modal from 'react-modal'; //npm install --save react-modal
import Swal from 'sweetalert2';
import { DndContext, closestCenter, PointerSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { LoadPanel } from 'devextreme-react/load-panel';
import { Popup } from 'devextreme-react/popup';
import { SelectBox } from 'devextreme-react/select-box';
import { getRequestParams } from '../../helpers/globalfunctions';
import { SortableReservationRow, formatDistance, formatDuration } from './ClubReservationCard';
import { ClubReservationsMap, buildRunStops } from './ClubReservationsMap';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import './ClubReservations.css';

const googleMapsApiKey = "AIzaSyCAiQ31UBwphPPI36Ty_nzZ27QxNx34SXQ";

// Ride coordinates arrive as floats that are 0 (or null) when unknown; treat those as "no location".
function toLatLng(lat, lng) {
    const la = Number(lat);
    const ln = Number(lng);
    if (!isFinite(la) || !isFinite(ln) || (la === 0 && ln === 0)) {
        return null;
    }
    return { lat: la, lng: ln };
}

// Self-contained loader — do NOT capture `window.google` at module-eval time (it isn't
// loaded yet on first import) and don't rely on any other component to have loaded it.
// Resolves once window.google.maps is actually usable; safe to call multiple times.
let googleMapsLoadPromise = null;
function loadGoogleMapsApi() {
    if (window.google && window.google.maps) {
        return Promise.resolve(window.google);
    }
    if (!googleMapsLoadPromise) {
        googleMapsLoadPromise = new Promise((resolve, reject) => {
            const scriptId = 'club-reservations-google-maps-script';
            const existingScript = document.getElementById(scriptId);
            if (existingScript) {
                existingScript.addEventListener('load', () => resolve(window.google));
                existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps JavaScript API')));
                return;
            }
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&v=3.exp&libraries=geometry,drawing,places`;
            script.async = true;
            script.onload = () => resolve(window.google);
            script.onerror = () => reject(new Error('Failed to load Google Maps JavaScript API'));
            document.body.appendChild(script);
        });
    }
    return googleMapsLoadPromise;
}

// Width of the popup while the map is hidden: just the reservation list, so it doesn't need
// the full map-and-list width. The side gaps grow on wide screens and never drop below 3%.
const LIST_ONLY_MAX_WIDTH = 1140;
// Map hidden: the popup is at least this tall, so a club of 2–3 passengers doesn't look cramped.
const LIST_ONLY_MIN_HEIGHT = '60vh';
// Popup height while the map is shown, relative to the screen width (popup width is 70vw → ~5:3).
const MAP_POPUP_HEIGHT = '42vw';

const getModalStyles = (isMapVisible) => ({
    content: {
        // Width: left/right anchors. Map shown: 15% each side (wide). Map hidden: at most
        // LIST_ONLY_MAX_WIDTH wide, centred.
        left: isMapVisible ? '15%' : `max(3%, calc(50% - ${LIST_ONLY_MAX_WIDTH / 2}px))`,
        right: isMapVisible ? '15%' : `max(3%, calc(50% - ${LIST_ONLY_MAX_WIDTH / 2}px))`,
        // Height. Map hidden: fits the content (up to the 4 passengers a club can have), between
        // LIST_ONLY_MIN_HEIGHT and 80% of the screen height. Map shown: follows the width instead (popup is 70vw wide, so 42vw is a
        // ~5:3 landscape box that gives the map room), never taller than 86% of the screen height.
        // Either way the list scrolls inside if it needs more.
        bottom: 'auto',
        height: isMapVisible ? MAP_POPUP_HEIGHT : 'auto',
        minHeight: isMapVisible ? undefined : LIST_ONLY_MIN_HEIGHT,
        maxHeight: isMapVisible ? '86vh' : '80vh',
        // Vertically centred. The shared ".react-modal" class (customstyle.css) uses
        // translate(-50%, -50%) with left:50%; our left/right anchors already centre horizontally,
        // so only the vertical half of that transform is kept.
        top: '50%',
        transform: 'translateY(-50%)',
        transition: 'left 0.2s ease, right 0.2s ease, height 0.2s ease',
        fontSize: '13px',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        // Clip to the popup, so the map (or anything else) can never show below its bottom edge.
        overflow: 'hidden',
    }
});

const position = { of: '#clubReservationsdiv' };

export class ClubReservationsComponent extends Component {
    static displayName = ClubReservationsComponent.name;

    constructor(props) {
        super(props);
        this.state = {
            isOpenModal: false,
            isSubmitting: false,
            // Which button started the submit ('assign' | 'confirm'), so only that one shows the spinner.
            submittingAction: null,
            loadPanelVisible: false,
            reservations: [],
            // Passenger whose pickup → drop-off route is drawn on the map (card or marker click).
            selectedRideId: null,
            // Run route segments in Seq order (stop k → stop k+1), drawn on the map; see calculateRoute.
            runSegments: [],
            legInfo: [],
            totalDistanceMeters: 0,
            totalDurationSeconds: 0,
            onClubbed: null,
            mapsReady: false,
            addressPopupVisible: false,
            addressPopupTitle: '',
            addressPopupText: '',
            // Vehicle/driver picked in the footer for "Club & Assign"
            dispatchVehicleId: 0,
            dispatchDriverId: 0,
            isMapVisible: false,
        };
        // Plain sensor descriptor — equivalent to useSensors(useSensor(PointerSensor)),
        // built without hooks since this is a class component.
        this.sensors = [{ sensor: PointerSensor, options: { activationConstraint: { distance: 5 } } }];

        this.showModal = this.showModal.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.handleDragEnd = this.handleDragEnd.bind(this);
        this.handleConfirm = this.handleConfirm.bind(this);
        this.handleClubAndAssign = this.handleClubAndAssign.bind(this);
        this.showAddressPopup = this.showAddressPopup.bind(this);
        this.handleSelectPassenger = this.handleSelectPassenger.bind(this);
    }

    componentDidMount() {
        // Kick off the Maps script load as soon as this modal component exists (it's always
        // mounted, even while closed) so it's likely ready by the time the dispatcher opens it.
        loadGoogleMapsApi()
            .then(() => this.setState({ mapsReady: true }))
            .catch(() => { /* handled per-call in loadRouteFor */ });
    }

    showModal(selectedRowsData, onClubbed) {
        // Map points come only from the ride's own coordinates, used exactly as stored:
        // pickupLatitude/pickupLongitude (shown with puStreet) and dropoffLatitude/dropoffLongitude (with doStreet).
        const reservations = selectedRowsData.map(r => ({
            rideId: r.rideId,
            tripNumber: r.jobId || r.rideId,
            paxName: r.paxName,
            gender: r.gender,
            pickupDateTime: r.pickupDateTime,
            puStreet: r.puStreet,
            doStreet: r.doStreet,
            pickup: toLatLng(r.pickupLatitude, r.pickupLongitude),
            dropoff: toLatLng(r.dropoffLatitude, r.dropoffLongitude),
        }));

        this.setState({
            isOpenModal: true,
            isSubmitting: false,
            reservations,
            selectedRideId: null,
            runSegments: [],
            legInfo: [],
            totalDistanceMeters: 0,
            totalDurationSeconds: 0,
            onClubbed,
            dispatchVehicleId: 0,
            dispatchDriverId: 0,
            isMapVisible: false,
        });

        this.loadRouteFor(reservations);
    }

    closeModal() {
        this.setState({
            isOpenModal: false,
            isSubmitting: false,
            loadPanelVisible: false,
            reservations: [],
            selectedRideId: null,
            runSegments: [],
            legInfo: [],
            totalDistanceMeters: 0,
            totalDurationSeconds: 0,
            onClubbed: null,
            addressPopupVisible: false,
            dispatchVehicleId: 0,
            dispatchDriverId: 0,
            isMapVisible: false,
        });
    }

    showAddressPopup(e, address, title) {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ addressPopupVisible: true, addressPopupText: address, addressPopupTitle: title });
    }

    // Selects a passenger (card or map marker) to draw their pickup → drop-off route. Clicking the
    // selected passenger again first reopens any of their map info windows closed with ✕; only when
    // none are closed does it clear the selection. The map itself is only shown/hidden by the Show Map
    // toggle; while it's hidden the selection is just kept, and the route appears once the map is shown.
    handleSelectPassenger(rideId) {
        if (this.state.selectedRideId === rideId && this.clubMap && this.clubMap.hasClosedInfoFor(rideId)) {
            this.clubMap.reopenInfoForSelected();
            return;
        }
        this.setState(prev => ({ selectedRideId: prev.selectedRideId === rideId ? null : rideId }));
    }

    // Loads the Maps script, then works out the run-order legs/total from the rides' own drop-off
    // coordinates (no address geocoding needed).
    async loadRouteFor(reservations) {
        this.setState({ loadPanelVisible: true });
        try {
            await loadGoogleMapsApi();
            this.setState({ mapsReady: true });
        } catch (e) {
            this.setState({ loadPanelVisible: false });
            await Swal.fire({
                icon: 'error',
                title: 'Map unavailable',
                text: 'Could not load Google Maps. The map and distances will not be available; you can still set the run order.',
                allowOutsideClick: false,
            });
            return;
        }
        this.calculateRoute(reservations);
    }

    // Run route in Seq order (card order): every pickup in sequence, then the drop-off(s) — see
    // buildRunStops. Each consecutive pair of stops (1 → 2, 2 → 3, …, last pickup → drop-off) is requested
    // as its own Directions route, so the drawn line always follows the sequence exactly (a single
    // multi-waypoint request came back skipping stops). The segments give the line on the map
    // (runSegments), the header Total, and the Leg pill above each card (the segments between that card's
    // stop and the previous card's). Re-run on open and after every drag-to-reorder.
    calculateRoute(reservations) {
        const noRoute = { runSegments: [], legInfo: [], totalDistanceMeters: 0, totalDurationSeconds: 0, loadPanelVisible: false };
        const { stops, cardStop } = buildRunStops(reservations);
        if (!window.google || !window.google.maps || stops.length < 2) {
            this.setState(noRoute);
            return;
        }

        // Ignore results from an older request if the dispatcher reordered in the meantime.
        const requestId = (this.routeRequestId || 0) + 1;
        this.routeRequestId = requestId;

        const directionsService = new window.google.maps.DirectionsService();
        // segment k: stops[k] → stops[k + 1]. `path` is the road path, or null when Google has no route
        // (the map then draws that one segment as a dashed straight line).
        const requestSegment = (from, to) => new Promise(resolve => {
            directionsService.route(
                { origin: from, destination: to, travelMode: window.google.maps.TravelMode.DRIVING },
                (result, status) => {
                    const leg = status === window.google.maps.DirectionsStatus.OK ? result.routes[0].legs[0] : null;
                    resolve({
                        from,
                        to,
                        path: leg ? result.routes[0].overview_path : null,
                        distanceMeters: leg ? leg.distance.value : null,
                        durationSeconds: leg ? leg.duration.value : null,
                    });
                }
            );
        });

        Promise.all(stops.slice(0, -1).map((from, k) => requestSegment(from, stops[k + 1]))).then(segments => {
            if (this.routeRequestId !== requestId) {
                return;
            }
            const allRouted = segments.every(s => s.path);
            // Sum of segments [from, to); null if any of them has no road route.
            const sumSegments = (from, to) => {
                const part = segments.slice(from, to);
                return part.every(s => s.path)
                    ? part.reduce((sum, s) => ({ distanceMeters: sum.distanceMeters + s.distanceMeters, durationSeconds: sum.durationSeconds + s.durationSeconds }), { distanceMeters: 0, durationSeconds: 0 })
                    : null;
            };
            const legInfo = reservations.map((r, i) => {
                const from = i > 0 ? cardStop[i - 1] : null;
                const to = cardStop[i];
                return from == null || to == null || to < from ? null : sumSegments(from, to);
            }).slice(1);
            const total = allRouted ? sumSegments(0, segments.length) : null;
            this.setState({
                runSegments: segments,
                legInfo,
                // Total only when every segment has a road route, so it's never a partial figure.
                totalDistanceMeters: total ? total.distanceMeters : 0,
                totalDurationSeconds: total ? total.durationSeconds : 0,
                loadPanelVisible: false,
            });
        });
    }

    handleDragEnd(event) {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = this.state.reservations.findIndex(r => r.rideId === active.id);
        const newIndex = this.state.reservations.findIndex(r => r.rideId === over.id);
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }
        const reordered = arrayMove(this.state.reservations, oldIndex, newIndex);
        this.setState({ reservations: reordered }, () => this.calculateRoute(reordered));
    }

    // Clubs the reservations in the current run order. Returns the API response data.
    async clubReservations() {
        const request = {
            // Array order reflects the run order the dispatcher set via drag-and-drop.
            Rides: this.state.reservations.map(r => ({ RideId: r.rideId, Gender: r.gender })),
            DeviceTime: new Date().toISOString(),
        };
        const requestParams = getRequestParams('POST', request);
        const response = await fetch('/dispatch-api/guard-multiple-rides', requestParams);
        return response.json();
    }

    // Closes the popup and lets DispatchComponent refresh the affected grid rows.
    async finishAndRefresh() {
        const rideIds = this.state.reservations.map(r => r.rideId);
        const onClubbed = this.state.onClubbed;
        this.closeModal();
        if (onClubbed) {
            await onClubbed(rideIds);
        }
    }

    async handleConfirm() {
        if (this.state.reservations.length < 2) {
            return;
        }
        this.setState({ isSubmitting: true, submittingAction: 'confirm' });

        try {
            const data = await this.clubReservations();

            await Swal.fire({
                icon: data.isSuccess ? 'success' : 'error',
                title: data.isSuccess ? 'Reservations Clubbed' : 'Oops...',
                text: data.resultMessage,
            });

            if (data.isSuccess) {
                await this.finishAndRefresh();
            } else {
                this.setState({ isSubmitting: false });
            }
        } catch (e) {
            this.setState({ isSubmitting: false });
        }
    }

    // Clubs the reservations, then assigns them to the vehicle/driver chosen in the footer
    // using the same assign-job API as DispatchComponent's Assign button (props.onAssign).
    async handleClubAndAssign() {
        if (this.state.reservations.length < 2) {
            return;
        }
        const { dispatchVehicleId, dispatchDriverId } = this.state;
        const missing = [];
        if (!dispatchDriverId) missing.push('driver');
        if (!dispatchVehicleId) missing.push('vehicle');
        if (missing.length > 0) {
            await Swal.fire({
                icon: 'warning',
                title: 'Club & Assign',
                text: `Please select a ${missing.join(' and a ')} before clubbing and assigning.`,
                allowOutsideClick: false,
            });
            return;
        }

        this.setState({ isSubmitting: true, submittingAction: 'assign' });
        try {
            const clubData = await this.clubReservations();
            if (!clubData.isSuccess) {
                await Swal.fire({ icon: 'error', title: 'Oops...', text: clubData.resultMessage });
                this.setState({ isSubmitting: false });
                return;
            }

            const rideIds = this.state.reservations.map(r => r.rideId);
            const assignData = await this.props.onAssign(rideIds, dispatchVehicleId, dispatchDriverId);
            await Swal.fire(assignData.isSuccess
                ? { icon: 'success', title: 'Reservations Clubbed & Assigned', text: assignData.resultMessage }
                // Clubbing already went through, so say so; the rides can be assigned from the grid.
                : { icon: 'warning', title: 'Clubbed, but not assigned', text: 'The reservations were clubbed, but assigning them failed: ' + assignData.resultMessage });

            // Either way the rides are now clubbed, so close and refresh their grid rows.
            await this.finishAndRefresh();
        } catch (e) {
            await Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong while clubbing and assigning the reservations.' });
            this.setState({ isSubmitting: false });
        }
    }

    render() {
        const { reservations, legInfo, totalDistanceMeters, totalDurationSeconds, isSubmitting, submittingAction, loadPanelVisible, mapsReady, isMapVisible, selectedRideId, runSegments } = this.state;
        const hasTotals = legInfo.length > 0;

        return (
            <Modal ariaHideApp={false}
                isOpen={this.state.isOpenModal}
                style={getModalStyles(isMapVisible)}
                className={"react-modal"}
                shouldCloseOnOverlayClick={false}
                contentLabel="Club Reservations"
            >
                <div id="clubReservationsdiv" className="cr-popup">
                    <div className="cr-header">
                        <div className="cr-header-title">
                            <span className="cr-header-icon"><i className="fas fa-link"></i></span>
                            <div>
                                <h6 className="cr-title">Club Reservations</h6>
                                <span className="cr-subtitle">Set run order &middot; {reservations.length} reservation{reservations.length === 1 ? '' : 's'}</span>
                            </div>
                        </div>
                        <div className="cr-header-actions">
                            {hasTotals &&
                                <span className="cr-total-pill" title="Total route distance and driving time">
                                    <i className="fas fa-route"></i> {formatDistance(totalDistanceMeters)}
                                    <span className="cr-leg-sep">&middot;</span>
                                    <i className="far fa-clock"></i> {formatDuration(totalDurationSeconds)}
                                </span>}
                            <button type="button" className="cr-close" aria-label="Close" title="Close" onClick={this.closeModal} disabled={isSubmitting}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>

                    {/* Action toolbar: vehicle/driver for "Club & Assign" (same lists and item renderers as
                        DispatchComponent's toolbar), then the actions and the map toggle. Wraps on narrow screens. */}
                    <div className="cr-toolbar">
                        <div className="cr-toolbar-group cr-toolbar-assign">
                            <span className="cr-toolbar-label"><i className="fas fa-user-tie"></i> Assign to</span>
                            <div className="cr-select cr-select-driver">
                                <SelectBox
                                    dataSource={this.props.serviceDrivers || []}
                                    placeholder="Select driver"
                                    value={this.state.dispatchDriverId || null}
                                    displayExpr="name"
                                    valueExpr="id"
                                    onSelectionChanged={(e) => this.setState({ dispatchDriverId: e.selectedItem ? e.selectedItem.id : 0 })}
                                    showClearButton={true}
                                    searchEnabled={true}
                                    searchExpr="name"
                                    searchMode="contains"
                                    wrapItemText={true}
                                    dropDownOptions={{ minWidth: 280 }}
                                    itemRender={this.props.driverItemRender}
                                    disabled={isSubmitting}
                                    height={32}
                                />
                            </div>
                            <div className="cr-select cr-select-vehicle">
                                <SelectBox
                                    dataSource={this.props.serviceVehicles || []}
                                    placeholder="Select vehicle"
                                    value={this.state.dispatchVehicleId || null}
                                    displayExpr="name"
                                    valueExpr="id"
                                    onSelectionChanged={(e) => this.setState({ dispatchVehicleId: e.selectedItem ? e.selectedItem.id : 0 })}
                                    showClearButton={true}
                                    searchEnabled={true}
                                    searchExpr="name"
                                    searchMode="contains"
                                    itemRender={this.props.vehicleItemRender}
                                    disabled={isSubmitting}
                                    height={32}
                                />
                            </div>
                        </div>
                        <div className="cr-toolbar-group cr-toolbar-actions">
                            <button type="button" className="btn btn-primary cr-btn" title="Club and assign to the selected vehicle/driver" onClick={this.handleClubAndAssign} disabled={isSubmitting || reservations.length < 2}>
                                {isSubmitting && submittingAction === 'assign' ? <span><i className="fas fa-spinner fa-spin"></i> In-process</span> : <span><i className="fas fa-link"></i> Club &amp; Assign</span>}
                            </button>
                            <button type="button" className="btn btn-success cr-btn" title="Club in this run order" onClick={this.handleConfirm} disabled={isSubmitting || reservations.length < 2}>
                                {isSubmitting && submittingAction === 'confirm' ? <span><i className="fas fa-spinner fa-spin"></i> In-process</span> : <span><i className="fas fa-check"></i> Confirm Order &amp; Club {reservations.length}</span>}
                            </button>
                            <button type="button" className="btn btn-outline-secondary cr-btn" title="Cancel" onClick={this.closeModal} disabled={isSubmitting}>
                                <i className="fas fa-times"></i> Cancel
                            </button>
                            {/* Off by default (map hidden). The label names what switching it will do. */}
                            <label className={`cr-map-toggle${isMapVisible ? ' cr-map-toggle-on' : ''}`} title={isMapVisible ? 'Hide the drop-off map' : 'Show the drop-off locations on the map'}>
                                <input type="checkbox" checked={isMapVisible} onChange={(e) => this.setState({ isMapVisible: e.target.checked })} />
                                <span className="cr-map-toggle-track"><span className="cr-map-toggle-thumb"></span></span>
                                <i className={isMapVisible ? 'fas fa-eye-slash' : 'fas fa-map-marked-alt'}></i>
                                {isMapVisible ? 'Hide Map' : 'Show Map'}
                            </label>
                        </div>
                    </div>

                    <div className={`cr-content${isMapVisible ? ' cr-content-with-map' : ''}`}>
                        <div className="cr-list-pane">
                            <div className="cr-list">
                                <p className="cr-hint"><i className="fas fa-grip-vertical"></i> Drag cards by the handle to set the order these {reservations.length} reservations should be run in.</p>
                                <DndContext sensors={this.sensors} collisionDetection={closestCenter} onDragEnd={this.handleDragEnd}>
                                    <SortableContext items={reservations.map(r => r.rideId)} strategy={verticalListSortingStrategy}>
                                        {reservations.map((r, index) => (
                                            <SortableReservationRow
                                                key={r.rideId}
                                                id={r.rideId}
                                                index={index}
                                                reservation={r}
                                                leg={index > 0 ? legInfo[index - 1] : null}
                                                onShowAddressPopup={this.showAddressPopup}
                                                isSelected={r.rideId === selectedRideId}
                                                onSelect={this.handleSelectPassenger}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                        </div>
                        {/* Mounted only while shown: a Google Map created inside a display:none container renders
                            grey/blank when revealed. The selected passenger stays in state, so their route redraws at once. */}
                        {isMapVisible &&
                            <div className="cr-map-pane">
                                <span className="cr-section-title">
                                    <i className="fas fa-map-marked-alt"></i> Run route
                                    <span className="cr-section-hint"> &mdash; pickups in Seq order, then drop-off. Select a passenger to highlight their location.</span>
                                </span>
                                {mapsReady ? (
                                    <ClubReservationsMap
                                        ref={(ref) => { this.clubMap = ref; }}
                                        reservations={reservations}
                                        selectedRideId={selectedRideId}
                                        runSegments={runSegments}
                                        onSelect={this.handleSelectPassenger}
                                    />
                                ) : (
                                    <div className="cr-map cr-map-loading">
                                        <i className="fas fa-spinner fa-spin"></i> Loading map...
                                    </div>
                                )}
                            </div>}
                    </div>
                </div>
                <LoadPanel
                    shadingColor="rgba(0,0,0,0.4)"
                    position={position}
                    visible={loadPanelVisible}
                    showIndicator={true}
                    shading={true}
                    showPane={true}
                    closeOnOutsideClick={false}
                />
                <Popup
                    visible={this.state.addressPopupVisible}
                    onHiding={() => this.setState({ addressPopupVisible: false })}
                    dragEnabled={false}
                    closeOnOutsideClick={true}
                    showTitle={true}
                    title={this.state.addressPopupTitle}
                    width={360}
                    height="auto"
                    showCloseButton={true}
                    contentRender={() => (
                        <div className="address-popup-content">
                            <i className="fas fa-map-marker-alt address-popup-marker"></i>
                            <span>{this.state.addressPopupText}</span>
                        </div>
                    )}
                />
            </Modal>
        );
    }
}
